import React, {useEffect} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useChekinSDK} from '../context/ChekinSDK';
import {
  QUERY_CACHE_KEYS,
  InsuranceSetup,
  SECURITY_DEPOSIT_STATUSES,
  useEvent,
} from '@chekin/core';
import {useReservation} from 'context/reservation';
import {usePaymentsStore} from 'store/payments';
import {API} from '@chekin/api';

const CACHE_TIME = 60_000;
const STALE_TIME = 60_000;

type UsePropertyProtection = {
  onError?: (error: Error) => void;
};
function usePropertyProtection({onError}: UsePropertyProtection = {}) {
  const queryClient = useQueryClient();
  const {data: reservation, housing, reservationId} = useReservation();
  const setProtectionPaymentPreloaded = usePaymentsStore(
    state => state.setProtectionPaymentPreloaded,
  );
  const handleError = useEvent(onError);
  const {isTokenValid} = useChekinSDK();

  const {
    data,
    error,
    isLoading: isPropertyProtectionsLoading,
    isFetching: isPropertyProtectionsFetching,
    isInitialLoading: isPropertyProtectionsInitialLoading,
    isSuccess: isPropertyProtectionsSuccess,
  } = useQuery({
    queryKey: [QUERY_CACHE_KEYS.propertyProtections, reservationId],
    queryFn: () =>
      API.propertyProtection.getPropertyProtectionsByReservation(reservationId!),
    enabled: Boolean(reservationId && isTokenValid),
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });

  const {
    data: insuranceSetup,
    error: insuranceSetupError,
    isLoading: isInsuranceSetupLoading,
    isFetching: isInsuranceSetupFetching,
    isInitialLoading: isInsuranceSetupInitialLoading,
    isSuccess: isInsuranceSetupSuccess,
  } = useQuery<InsuranceSetup>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.insuranceSetup],
    queryFn: () =>
      API.propertyProtection.getInsuranceSetup(reservation?.insurance_setup_id || ''),
    enabled: Boolean(reservation?.insurance_setup_id && isTokenValid),
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });

  const errorToPass = error || insuranceSetupError;
  useEffect(() => {
    if (errorToPass) {
      handleError(errorToPass);
    }
  }, [errorToPass, handleError]);

  const isInsuranceFirstLoading =
    Boolean(isInsuranceSetupLoading && !reservation?.id) ||
    Boolean(
      isInsuranceSetupLoading && reservation?.id && reservation?.insurance_setup_id,
    ) ||
    isInsuranceSetupInitialLoading;

  const {mutate, isPending: isInsuranceSetupMutation} = useMutation<
    InsuranceSetup,
    Error,
    Partial<InsuranceSetup>
  >({
    mutationFn: payload => API.propertyProtection.mutateInsuranceSetup(payload),
    onSuccess: mutatedInsuranceSetup => {
      setProtectionPaymentPreloaded(false);
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.paymentsCart],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_CACHE_KEYS.summary],
      });
      queryClient.setQueryData([QUERY_CACHE_KEYS.insuranceSetup], mutatedInsuranceSetup);
    },
    onError,
  });

  const createOrUpdateInsuranceSetup = React.useCallback(
    (
      payload: Pick<InsuranceSetup, 'insurance_payment_type' | 'insurance_type'>,
      options: Parameters<typeof mutate>[1],
    ) => {
      const builtPayload = {reservation: reservationId, ...payload};
      if (insuranceSetup?.id) {
        return mutate({id: insuranceSetup?.id, ...builtPayload}, options);
      }

      return mutate(builtPayload, options);
    },
    [insuranceSetup?.id, mutate, reservationId],
  );

  const isSecurityDepositOptional =
    data?.security_deposit?.security_deposit_status ===
    SECURITY_DEPOSIT_STATUSES.optional;

  const isWaivoProtectionMandatory =
    housing?.damage_protection_status === SECURITY_DEPOSIT_STATUSES.mandatory;

  const isMultipleDeposits =
    Boolean(data?.security_deposit) && Boolean(data?.waivo_damage_protection);
  const isLoading =
    isPropertyProtectionsLoading ||
    isPropertyProtectionsFetching ||
    isInsuranceSetupFetching ||
    isInsuranceSetupMutation;

  const isSecurityDeposit = Boolean(data?.security_deposit) ?? undefined;
  const isWaivoProtection = Boolean(data?.waivo_damage_protection) ?? undefined;

  return {
    data,
    isLoading,
    isFirstLoading: isPropertyProtectionsInitialLoading || isInsuranceFirstLoading,
    isFirstLoaded: isPropertyProtectionsSuccess || isInsuranceSetupSuccess,
    isInsuranceSetupInit: reservation?.insurance_setup_id
      ? isInsuranceSetupSuccess
      : !!reservation?.id,
    isInsuranceSetupLoading,
    insuranceSetup,
    isMultipleDeposits,
    isSecurityDepositOptional,
    isWaivoProtectionMandatory,
    isSecurityDeposit,
    isWaivoProtection,
    createOrUpdateInsuranceSetup,
  };
}

export {usePropertyProtection};
